import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "motion/react";
import { getChampionImage } from "../../utils/team";

const T = {
    walking: 2400,
    looking: 2400,
    jaxAppears: 600,
    spotted: 350,
    running: 1000,
    afterRun: 400,
    leaving: 500,
};

type Phase =
    | "walking"
    | "looking"
    | "jaxAppears"
    | "spotted"
    | "running"
    | "leaving";

interface Props {
    active: boolean;
    onComplete: () => void;
}

export const JaxAndAnnie = ({ active, onComplete }: Props) => {
    const [phase, setPhase] = useState<Phase>("walking");

    useEffect(() => {
        if (!active) return;
        setPhase("walking");

        const timers: ReturnType<typeof setTimeout>[] = [];
        let t = 0;
        t += T.walking;
        timers.push(setTimeout(() => setPhase("looking"), t));
        t += T.looking;
        timers.push(setTimeout(() => setPhase("jaxAppears"), t));
        t += T.jaxAppears;
        timers.push(setTimeout(() => setPhase("spotted"), t));
        t += T.spotted;
        timers.push(setTimeout(() => setPhase("running"), t));
        t += T.running + T.afterRun;
        timers.push(setTimeout(() => setPhase("leaving"), t));
        t += T.leaving;
        timers.push(setTimeout(() => onComplete(), t));

        return () => timers.forEach(clearTimeout);
    }, [active, onComplete]);

    const annieAnimate =
        phase === "walking"
            ? { x: 0, y: [0, -8, 0, -8, 0, -8, 0, -8, 0] }
            : phase === "looking"
              ? { x: 0, y: 0, scaleX: [1, 1, -1, -1, 1, 1, -1, -1, 1] }
              : phase === "jaxAppears"
                ? { x: 0, y: 0, scaleX: 1 }
                : phase === "spotted"
                  ? { x: 0, y: -22, rotate: -10, scaleX: 1 }
                  : phase === "running"
                    ? {
                          x: 320,
                          y: [-14, -30, -14, -30, -14, -30, -14, -30, -14],
                          rotate: [0, 6, 0, -6, 0],
                          scaleX: 1,
                      }
                    : { x: 320, opacity: 0 };

    const annieTransition =
        phase === "walking"
            ? { duration: T.walking / 1000, ease: "linear" as const }
            : phase === "looking"
              ? {
                    duration: T.looking / 1000,
                    times: [0, 0.12, 0.13, 0.37, 0.38, 0.62, 0.63, 0.87, 0.88, 1],
                }
              : phase === "jaxAppears"
                ? { duration: T.jaxAppears / 1000 }
                : phase === "spotted"
                  ? { duration: T.spotted / 1000 }
                  : phase === "running"
                    ? { duration: T.running / 1000, ease: "easeIn" as const }
                    : { duration: T.leaving / 1000 };

    const jaxVisible =
        phase === "jaxAppears" ||
        phase === "spotted" ||
        phase === "running" ||
        phase === "leaving";

    return (
        <AnimatePresence>
            {active && (
                <motion.img
                    key="annie"
                    src={getChampionImage("Annie")}
                    alt="Annie"
                    initial={{ x: 240, y: 0, scaleX: 1, rotate: 0, opacity: 1 }}
                    animate={annieAnimate}
                    transition={annieTransition}
                    exit={{ opacity: 0, transition: { duration: 0.3 } }}
                    style={{
                        position: "fixed",
                        bottom: 32,
                        right: 24,
                        width: 140,
                        height: 140,
                        borderRadius: "50%",
                        border: "3px solid #bf1e36",
                        boxShadow: "0 0 24px rgba(191, 30, 54, 0.45)",
                        zIndex: 9999,
                        pointerEvents: "none",
                    }}
                />
            )}
            {active && jaxVisible && (
                <motion.img
                    key="jax"
                    src={getChampionImage("Jax")}
                    alt="Jax"
                    initial={{ x: -260, opacity: 0, rotate: 0 }}
                    animate={
                        phase === "leaving"
                            ? { x: -260, opacity: 0 }
                            : {
                                  x: -40,
                                  opacity: 1,
                                  rotate: [0, -4, 4, -3, 3, 0],
                              }
                    }
                    transition={
                        phase === "leaving"
                            ? { duration: T.leaving / 1000 }
                            : {
                                  x: { type: "spring", stiffness: 160, damping: 16 },
                                  opacity: { duration: 0.3 },
                                  rotate: {
                                      duration: 2,
                                      repeat: Infinity,
                                      ease: "easeInOut",
                                  },
                              }
                    }
                    exit={{ x: -260, opacity: 0, transition: { duration: 0.4 } }}
                    style={{
                        position: "fixed",
                        bottom: 32,
                        left: 0,
                        width: 180,
                        height: 180,
                        borderRadius: "50%",
                        border: "3px solid #0a97ab",
                        boxShadow: "0 0 32px rgba(10, 151, 171, 0.45)",
                        zIndex: 9999,
                        pointerEvents: "none",
                    }}
                />
            )}
        </AnimatePresence>
    );
};
